@let msg = message();
@let isUser = isFromCurrentUser();
@let html = sanitizedContent();

@if (isUser) {
    <qpx-avatar
        class="avatar justify-self-center"
        [class.shadow-xs]="appearance() === 'elevated'"
        [initials]="userInitials() || 'U'"
        [colorKey]="userInitials() || 'user'"
    />
} @else {
    <qpx-avatar
        class="avatar justify-self-center bg-background!"
        [class.shadow-xs]="appearance() === 'elevated'"
        [class.outlined]="appearance() === 'outlined'"
        imageUrl="assets/media/quippy-headshot.svg"
        fallbackIcon="lucideCircleHelp"
    />
}

<div class="content flex min-w-0 flex-col gap-1.5">
    <div
        class="bubble"
        [class.shadow-xs]="appearance() === 'elevated'"
        [class.outlined]="
            appearance() === 'outlined' && !isUser && msg.status !== 'error'
        "
    >
        @if (isUser && isEditing()) {
            <qpx-quippy-message-edit-panel
                [appearance]="appearance()"
                [messageId]="msg.id"
                [draft]="editDraft()"
                [canResend]="canResendEdit()"
                [fieldLabel]="editFieldLabel()"
                (draftChanged)="onEditInput($event)"
                (editCancelled)="onCancelEdit()"
                (resendRequested)="onResendEdit()"
            />
        } @else if (isUser) {
            <div
                class="prose prose-sm prose-qpx max-w-none break-words prose-h1:text-heading-lg prose-h2:text-modal-title prose-h3:text-heading-sm"
                [innerHTML]="html"
            ></div>
        } @else {
            @switch (msg.status) {
                @case ('pending') {
                    <qpx-quippy-typing-indicator />
                }
                @case ('error') {
                    <p qpxBody class="m-0">
                        {{ msg.content }}
                    </p>
                }
                @default {
                    <div
                        class="prose prose-sm prose-qpx max-w-none break-words prose-h1:text-heading-lg prose-h2:text-modal-title prose-h3:text-heading-sm"
                        [innerHTML]="html"
                    ></div>
                    @if (hasDocumentOptions()) {
                        <ul
                            qpx-quippy-message-document-options
                            [message]="msg"
                            (optionSelected)="onDocumentOptionSelected($event)"
                        ></ul>
                    }
                }
            }
        }
    </div>

    @if (!isUser && msg.sources.length > 0) {
        <qpx-quippy-related-documents
            [appearance]="appearance()"
            [sourceSetId]="msg.id"
            [sources]="msg.sources"
            [sourceFallbackBuildingId]="sourceFallbackBuildingId()"
        />
    }

    @if (!isUser && msg.packAction) {
        <qpx-quippy-pack-action
            [appearance]="appearance()"
            [actionId]="msg.id"
            [proposal]="msg.packAction"
        />
    }

    @if (!isPending()) {
        <div class="meta-row flex h-4 items-center gap-2 px-1">
            <time
                class="block text-button-xs font-normal text-muted"
                [attr.datetime]="msg.timestamp | qpxToLocalISO"
            >
                {{ msg.timestamp | qpxRelativeDate: now() }}
            </time>

            @if (showActions()) {
                <fieldset
                    class="m-0 flex min-w-0 items-center gap-1 border-0 p-0"
                >
                    <legend class="sr-only">Message actions</legend>
                    @if (isUser) {
                        <button
                            qpxButton
                            matButton
                            size="xs"
                            context="secondary"
                            tone="muted"
                            [disabled]="isThreadLoading() || isEditing()"
                            [attr.aria-label]="editActionLabel()"
                            (click)="onStartEdit()"
                            #editButton
                        >
                            <ng-icon
                                qpx
                                size="xs"
                                name="lucidePencil"
                                matButtonIcon
                            />
                            <span>Edit</span>
                        </button>
                    } @else {
                        <button
                            qpxButton
                            matButton
                            size="xs"
                            [disabled]="isThreadLoading()"
                            context="secondary"
                            tone="muted"
                            [attr.aria-label]="retryActionLabel()"
                            (click)="onRetry()"
                        >
                            <ng-icon
                                qpx
                                size="xs"
                                name="lucideRefreshCw"
                                matButtonIcon
                            />
                            <span>Retry</span>
                        </button>
                    }
                    <button
                        qpxButton
                        matButton
                        size="xs"
                        context="secondary"
                        tone="muted"
                        [attr.aria-label]="copyActionLabel()"
                        (click)="onCopy()"
                    >
                        <ng-icon
                            qpx
                            size="xs"
                            name="lucideCopy"
                            matButtonIcon
                        />
                        <span>Copy</span>
                    </button>
                </fieldset>
            }
        </div>
    }
</div>
